import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import {
  useGetPurchaseOrderNotesQuery,
  useDeletePurchaseOrderNoteMutation,
} from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { exportPurchaseOrderPdf } from '../utils/exportPurchaseOrderPdf';

function PurchaseOrderNotesList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [deleteDialogOrder, setDeleteDialogOrder] = useState(null);

  const toApiDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate(),
          10
        ).padStart(2, '0')}`
      : undefined;

  const { data, isLoading, error } = useGetPurchaseOrderNotesQuery({
    page,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    search: search || undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });

  const [deletePurchaseOrderNote, { isLoading: isDeleting }] = useDeletePurchaseOrderNoteMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
        maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd':
        return `${yyyy}-${mm}-${dd}`;
      case 'dd-MM-yyyy':
        return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  const getStatusChip = (status) => {
    const configs = {
      draft: { label: 'Draft', color: 'default' },
      sent: { label: 'Sent', color: 'info' },
      confirmed: { label: 'Confirmed', color: 'primary' },
      converted: { label: 'Converted to Purchase', color: 'success' },
      cancelled: { label: 'Cancelled', color: 'error' },
    };
    const c = configs[status] || { label: status, color: 'default' };
    return (
      <Chip
        label={c.label}
        color={c.color}
        size="small"
        sx={{ fontWeight: 600, textTransform: 'capitalize' }}
      />
    );
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialogOrder) return;
    try {
      await deletePurchaseOrderNote(deleteDialogOrder.id).unwrap();
      setDeleteDialogOrder(null);
    } catch (e) {
      alert(e?.data?.message || 'Failed to delete purchase order note');
    }
  };

  if (isLoading) return <Typography>Loading purchase orders...</Typography>;
  if (error) return <Typography color="error">Error loading purchase orders</Typography>;

  const orderNotes = data?.purchaseOrderNotes || [];
  const pagination = data?.pagination;

  return (
    <Box>
      {/* Top Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Purchase Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Order notes sent to suppliers. Does not affect stock or accounting ledgers until converted.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate('/purchases')}
            sx={{ borderRadius: 2 }}
          >
            Purchases / Invoices
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/purchases/returns')}
            sx={{ borderRadius: 2 }}
          >
            Returns
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/purchases/suppliers')}
            sx={{ borderRadius: 2 }}
          >
            Suppliers
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchases/order-notes/new')}
            sx={{ borderRadius: 2 }}
          >
            New Purchase Order
          </Button>
        </Box>
      </Box>

      {/* Main Card with Filters and Table */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Search (PO #, Supplier, Notes)"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 240 }}
                />
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="converted">Converted</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(val) => {
                    setFromDate(val);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(val) => {
                    setToDate(val);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>
              {pagination && (
                <Typography variant="body2" color="text.secondary">
                  {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
                </Typography>
              )}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expected Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Items
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderNotes.length > 0 ? (
                    orderNotes.map((note) => {
                      const canConvert = note.status !== 'converted' && note.status !== 'cancelled';
                      const canEdit = note.status !== 'converted';

                      return (
                        <TableRow key={note.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => navigate(`/purchases/order-notes/${note.id}`)}
                            >
                              {note.poNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{note.supplier?.name || '-'}</TableCell>
                          <TableCell>{formatDate(note.orderDate)}</TableCell>
                          <TableCell>{formatDate(note.expectedDeliveryDate)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${note.items?.length || 0} items`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatAmount(note.grandTotal)}
                          </TableCell>
                          <TableCell>{getStatusChip(note.status)}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              {canConvert && (
                                <Tooltip title="Convert to Purchase (Pre-fill Bill)">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() =>
                                      navigate(`/purchases/orders/new?fromPoId=${note.id}`)
                                    }
                                  >
                                    <ShoppingCartCheckoutIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => navigate(`/purchases/order-notes/${note.id}`)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {canEdit && (
                                <Tooltip title="Edit Purchase Order">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() =>
                                      navigate(`/purchases/order-notes/${note.id}/edit`)
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Print / PDF">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => exportPurchaseOrderPdf(note, company)}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteDialogOrder(note)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1" color="text.secondary">
                          No purchase orders found
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/purchases/order-notes/new')}
                          sx={{ mt: 2, borderRadius: 2 }}
                        >
                          Create First Purchase Order
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && pagination.total > 0 && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={page - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            )}
          </CardContent>
        </Card>
      </LocalizationProvider>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteDialogOrder)} onClose={() => setDeleteDialogOrder(null)}>
        <DialogTitle>Delete Purchase Order?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete purchase order{' '}
            <strong>{deleteDialogOrder?.poNumber}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Note: Deleting a purchase order does not affect any stock or accounting ledger balances.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOrder(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchaseOrderNotesList;
